/*
 * Do not remove or alter the notices in this preamble.
 * This software code regards iO Custom Software.
 * Copyright © 2026 iO The Netherlands B.V. and/or its affiliates.
 * www.iodigital.com All rights reserved. License grant and user rights and obligations
 * according to applicable license agreement. Please contact legal@iodigital.com for
 * questions regarding license and user rights.
 */

import { t } from 'i18next';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { createRef, Ref, ref } from 'lit/directives/ref.js';
import debounce from 'lodash-es/debounce';

import {
  iconPdf,
  iconPencil,
  iconSave,
  iconTrashCan,
} from '@/components/icons';
import { BREAKPOINT_EXTRA_LARGE_PX } from '@/settings';
import {
  baseStyles,
  buttonStyles,
  formStyles,
  typographyStyles,
} from '@/styles';
import { formatDate, getCustomerName, i18nInit, setBaseUrl } from '@/utils';

import {
  DebtorCollectionDetailDto,
  DebtorCollectionProductDto,
} from '@/api-sdk';
import { placeholderBackup } from '@/assets';
import '@/components/image/fg-image';
import '@/components/messages/fg-message';
import '@/components/pagination/fg-pagination';
import '@/components/skeleton-loader/collection-details-skeleton-loader';
import {
  fgCollectionDetailsStore,
  PriceMode,
  type FgCollectionDetailsState,
} from '@/stores/fg-collection-details.store';

@customElement('fg-collection-details')
export class FgCollectionDetails extends LitElement {
  private collectionDetailsStore = fgCollectionDetailsStore.getState();
  private _storeUnsub?: () => void;

  @property({ type: String })
  baseUrl = '';

  @property({ type: String })
  locale = 'en_US';

  @property({ type: String })
  country = 'US';

  @property({ type: String })
  debtor = '';

  @property({ type: Number })
  customerId?: number;

  @property({ type: Object })
  customers: { [id: string]: string } = {};

  @property({ type: String })
  uuid = '';

  @property({ type: Object })
  collectionDetails: DebtorCollectionDetailDto | null =
    this.collectionDetailsStore.collectionDetails;

  @property({ type: Boolean })
  isLoading = this.collectionDetailsStore.isLoading;

  @property({ type: Number })
  currentPage = this.collectionDetailsStore.currentPage;

  @property({ type: Boolean })
  isEditingName = this.collectionDetailsStore.isEditingName;

  @property({ type: String })
  priceMode: PriceMode = this.collectionDetailsStore.priceMode;

  @property({ type: String })
  margin: string = this.collectionDetailsStore.margin;

  @property({ type: String })
  error: string | null = this.collectionDetailsStore.error;

  private titleRef: Ref<HTMLHeadingElement> = createRef();
  private fileInputRef: Ref<HTMLInputElement> = createRef();
  private marginInputRef: Ref<HTMLInputElement> = createRef();

  connectedCallback() {
    super.connectedCallback();
    this._storeUnsub = fgCollectionDetailsStore.subscribe(
      (state: FgCollectionDetailsState) => {
        this.collectionDetails = state.collectionDetails;
        this.isEditingName = state.isEditingName;
        this.isLoading = state.isLoading;
        this.currentPage = state.currentPage;
        this.priceMode = state.priceMode;
        this.margin = state.margin;
        this.error = state.error;
      },
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._storeUnsub?.();
    this.handleMarginUpdate.cancel();
  }

  willUpdate() {
    this.collectionDetailsStore.setConfig({
      baseUrl: this.baseUrl,
      locale: this.locale,
      country: this.country,
      debtor: this.debtor,
      customerId: this.customerId,
      uuid: this.uuid,
    });
  }

  async scheduleUpdate() {
    setBaseUrl(this.baseUrl);

    window.i18next = window.i18next || i18nInit(this.locale);
    await window.i18next;

    super.scheduleUpdate();
  }

  async firstUpdated() {
    await this.collectionDetailsStore.getCollectionDetails();
  }

  async handleSaveCollectionName() {
    const title = this.titleRef.value;
    const newName = title?.textContent?.trim() ?? '';

    const { saveCollectionName } = this.collectionDetailsStore;
    try {
      await saveCollectionName(newName);
    } catch {
      console.error('Error saving collection name');
    }
  }

  handleTitleKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.handleSaveCollectionName();
    }
  }

  handleEditCollectionName() {
    const { setEditingName } = this.collectionDetailsStore;
    setEditingName(true);
    this.updateComplete.then(() => {
      const titleElement = this.titleRef.value;
      if (!titleElement) return;

      titleElement.focus();
      window.getSelection()?.selectAllChildren(titleElement);
      window.getSelection()?.collapseToEnd();
    });
  }

  handlePriceModeChange(mode: PriceMode) {
    const { setPriceMode } = this.collectionDetailsStore;
    const inputEl = this.marginInputRef.value;
    let marginValue = inputEl?.value || '0';

    if (mode === PriceMode.WITH_MARGIN && !inputEl?.value) {
      marginValue = inputEl?.placeholder || '30';
      if (inputEl !== undefined) {
        inputEl.value = marginValue;
      }
    }

    setPriceMode(mode, marginValue);
  }

  handleMarginUpdate = debounce(() => {
    if (this.priceMode !== PriceMode.WITH_MARGIN) return;
    const { setPriceMode } = this.collectionDetailsStore;
    const marginValue = this.marginInputRef.value?.value || '0';
    setPriceMode(PriceMode.WITH_MARGIN, marginValue);
  }, 500);

  private renderPrice(
    label: string,
    price: DebtorCollectionProductDto['unitPrice'],
    marginPrice: DebtorCollectionProductDto['marginUnitPrice'],
    showMargin: boolean,
  ) {
    return html`
      <span class="price">
        <span>${label}</span>
        ${showMargin
          ? html`
              <s>${price?.formatted ?? '-'}</s>
              <span class="margin-price">${marginPrice?.formatted}</span>
            `
          : html`
              ${price?.formatted ?? '-'}
            `}
      </span>
    `;
  }

  handleImageUploadClick() {
    this.fileInputRef.value?.click();
  }

  async handleImageFileChange() {
    const file = this.fileInputRef.value?.files?.[0];
    if (!file) return;

    const { uploadCollectionImage } = this.collectionDetailsStore;
    await uploadCollectionImage(file);

    if (this.fileInputRef.value) {
      this.fileInputRef.value.value = '';
    }
  }

  render() {
    const { removeProductFromCollection, setRemark, generatePdf } =
      this.collectionDetailsStore;
    if (this.isLoading) {
      return html`
        <fg-collection-details-skeleton-loader></fg-collection-details-skeleton-loader>
      `;
    }

    if (this.error) {
      return html`
        <fg-message
          part="wholesale"
          .message=${t(this.error) || ''}
          type="error"
        ></fg-message>
      `;
    }

    if (!this.collectionDetails) {
      return nothing;
    }

    const { name, createdAt, createdBy, imageUrl, products } =
      this.collectionDetails;
    const formattedDate = createdAt ? formatDate(createdAt, this.locale) : '';

    return html`
      <div class="collection-details-header">
        <input
          ${ref(this.fileInputRef)}
          type="file"
          accept="image/jpeg,image/png"
          hidden
          @change=${() => this.handleImageFileChange()}
        />
        <button
          class="collection-details-header-image ${imageUrl
            ? ''
            : 'has-placeholder'}"
          @click=${() => this.handleImageUploadClick()}
        >
          <fg-image
            src=${imageUrl || placeholderBackup}
            .placeholder=${placeholderBackup}
            alt=""
          ></fg-image>
          <span class="icon">${iconPencil}</span>
          <span class="sr-only">${t('actions.editImage')}</span>
        </button>
        <hgroup>
          <h1
            ${ref(this.titleRef)}
            contenteditable=${this.isEditingName ? 'true' : 'false'}
            @keydown=${this.isEditingName
              ? (event: KeyboardEvent) => this.handleTitleKeydown(event)
              : nothing}
          >
            ${name}
          </h1>
          <button
            class=${this.isEditingName ? 'is-editing' : ''}
            @click=${this.isEditingName
              ? () => this.handleSaveCollectionName()
              : () => this.handleEditCollectionName()}
          >
            ${this.isEditingName ? iconSave : iconPencil}
            <span class="sr-only">
              ${this.isEditingName
                ? t('actions.save')
                : t('actions.editCollectionName')}
            </span>
          </button>
          <p>
            ${t('labels.createdOnDateByAuthor', {
              date: formattedDate,
              author: getCustomerName(this.customers, createdBy) ?? createdBy,
            })}
          </p>
        </hgroup>
      </div>
      <section class="settings">
        <fieldset>
          <legend>${t('labels.pricesShown')}</legend>
          <div>
            <span>
              <input
                type="radio"
                name="show-prices"
                value="without-margin"
                id="without-margin"
                ?checked=${this.priceMode === PriceMode.WITHOUT_MARGIN}
                @change=${() =>
                  this.handlePriceModeChange(PriceMode.WITHOUT_MARGIN)}
              />
              <label for="without-margin">${t('labels.purchasePrices')}</label>
            </span>
            <span>
              <input
                type="radio"
                name="show-prices"
                value="with-margin"
                id="with-margin"
                ?checked=${this.priceMode === PriceMode.WITH_MARGIN}
                @change=${() =>
                  this.handlePriceModeChange(PriceMode.WITH_MARGIN)}
              />
              <label for="with-margin">${t('labels.pricesWithMargin')}</label>
              <span class="margin-percentage">
                <input
                  ${ref(this.marginInputRef)}
                  type="text"
                  placeholder="30"
                  id="margin-percentage"
                  .value=${this.margin === '0' ? '' : this.margin}
                  @input=${() => this.handleMarginUpdate()}
                />
                <label for="margin-percentage">
                  <span class="sr-only">
                    ${t('labels.totalMarginInPercent')}
                  </span>
                  %
                </label>
              </span>
            </span>
            <span>
              <input
                type="radio"
                name="show-prices"
                value="no-prices"
                id="no-prices"
                ?checked=${this.priceMode === PriceMode.NO_PRICES}
                @change=${() => this.handlePriceModeChange(PriceMode.NO_PRICES)}
              />
              <label for="no-prices">${t('labels.noPrices')}</label>
            </span>
          </div>
        </fieldset>
        <div class="remarks">
          <div>
            <label>${t('labels.remarksOnFrontOfPDF')}</label>
            <textarea
              name="remarks-front-of-pdf"
              placeholder="${ifDefined(
                t('labels.addARemarkMoreLinesAreAllowed') || undefined,
              )}"
              @input=${(e: InputEvent) =>
                setRemark('front', (e.target as HTMLTextAreaElement).value)}
            ></textarea>
          </div>
          <div>
            <label>${t('labels.remarksOnBackOfPDF')}</label>
            <textarea
              name="remarks-back-of-pdf"
              placeholder="${ifDefined(
                t('labels.addARemarkMoreLinesAreAllowed') || undefined,
              )}"
              @input=${(e: InputEvent) =>
                setRemark('back', (e.target as HTMLTextAreaElement).value)}
            ></textarea>
          </div>
        </div>
        <button class="button button-cta" @click=${() => generatePdf()}>
          ${iconPdf} ${t('actions.generatePDF')}
        </button>
      </section>
      <section class="collection-contents">
        <div class="table-header">
          <span>${t('labels.article')}</span>
          <span>${t('labels.pricePerItemUnprinted')}</span>
          <span>${t('labels.printPriceFrom')}</span>
        </div>
        <div class="table-body">
          ${!products?.items?.length
            ? html`
                <fg-message
                  part="wholesale"
                  .message=${t('content.collection.noProducts') || ''}
                  type="info"
                ></fg-message>
              `
            : nothing}
          ${products?.items?.map((item: DebtorCollectionProductDto) => {
            const {
              code,
              name,
              thumbnail,
              unitPrice,
              marginUnitPrice,
              printPrice,
              marginPrintPrice,
            } = item;
            const showMarginPrices = this.priceMode === PriceMode.WITH_MARGIN;
            return html`
              <div class="table-row">
                <span>
                  <span class="image-container">
                    ${thumbnail
                      ? html`
                          <fg-image
                            src=${thumbnail}
                            width="64"
                            height="64"
                            alt="${name}"
                          ></fg-image>
                        `
                      : nothing}
                  </span>
                  <span>
                    <span>${code}</span>
                    <span>${name}</span>
                  </span>
                </span>
                ${this.renderPrice(
                  t('labels.pricePerItemUnprinted'),
                  unitPrice,
                  marginUnitPrice,
                  showMarginPrices,
                )}
                ${this.renderPrice(
                  t('labels.printPriceFrom'),
                  printPrice,
                  marginPrintPrice,
                  showMarginPrices,
                )}
                <span>
                  <button @click=${() => removeProductFromCollection(code)}>
                    <span>${iconTrashCan}</span>
                    <span class="sr-only">
                      ${t('actions.removeFromCollection', {
                        article: name,
                      })}
                    </span>
                  </button>
                </span>
              </div>
            `;
          })}
        </div>
        ${products?.pages > 1
          ? html`
              <fg-pagination
                .pages=${products.pages}
                .currentPage=${this.currentPage}
                @go-to-page=${(event: CustomEvent<{ page: number }>) => {
                  const { page } = event.detail;

                  if (page) {
                    const { setCurrentPage, getCollectionDetails } =
                      this.collectionDetailsStore;
                    setCurrentPage(page);
                    getCollectionDetails();
                    this.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
              ></fg-pagination>
            `
          : nothing}
      </section>
      <!--https://design-system.service.gov.uk/components/character-count/-->
    `;
  }

  static styles = [
    baseStyles,
    buttonStyles,
    formStyles,
    typographyStyles,
    css`
      .collection-details-header {
        display: grid;
        column-gap: 0.75rem;
        grid-template-columns: 92px 1fr;
        margin-bottom: 1.5rem;
      }

      .image-container {
        align-self: start;
        aspect-ratio: 1;
        display: block;
        position: relative;
        background-color: #ffffff;
        border: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
        border-radius: var(
          --fg-borderradius--sm,
          var(--fg-fallback-borderradius--sm)
        );
      }

      .image-container fg-image {
        block-size: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        inline-size: 100%;
      }

      .image-container fg-image::part(image) {
        block-size: 48px;
        object-fit: contain;
      }

      :host .collection-details-header > button {
        background-color: transparent;
        border: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
        border-radius: 46px;
        cursor: pointer;
        overflow: hidden;
        position: relative;
        height: 92px;
        width: 92px;
      }

      .collection-details-header .icon {
        bottom: 4px;
        left: 50%;
        position: absolute;
        transform: translateX(-50%);
        z-index: 2;
      }

      .collection-details-header > button::before {
        background: linear-gradient(
          0deg,
          var(--fg-primary--light, var(--fg-fallback-primary--light)) 29%,
          transparent 29%
        );
        bottom: 0;
        content: '';
        display: block;
        left: 0;
        position: absolute;
        height: 92px;
        width: 92px;
        z-index: 1;
      }

      .collection-details-header button fg-image {
        display: block;
        height: 100%;
        position: relative;
        width: 100%;
        z-index: 0;
      }

      .collection-details-header button fg-image::part(image) {
        display: block;
        height: 100%;
        object-fit: contain;
        object-position: center;
        width: 100%;
      }

      .collection-details-header button.has-placeholder fg-image::part(image) {
        padding: 1rem;
      }

      hgroup {
        align-items: center;
        column-gap: 0.5rem;
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        padding: 0.75rem 0;
      }

      hgroup h1 {
        font-size: 2.25rem;
        font-weight: 700;
        margin: 0;
      }

      hgroup h1[contenteditable='true'] {
        border-bottom: 1px solid
          var(--fg-gray--300, var(--fg-fallback-gray--300));
      }

      hgroup button {
        background-color: var(--fg-gray--100, var(--fg-fallback-gray--100));
        border-radius: 0.75rem;
        color: var(--fg-secondary, var(--fg-fallback-secondary));
        display: grid;
        height: 1.5rem;
        place-items: center;
        width: 1.5rem;
      }

      hgroup button svg {
        height: 12px;
        width: 12px;
      }

      hgroup p {
        width: 100%;
      }

      .settings {
        background-color: var(--fg-gray--100, var(--fg-fallback-gray--100));
        border-radius: var(
          --fg-borderradius--sm,
          var(--fg-fallback-borderradius--sm)
        );
        font-size: 1rem;
        padding: 1rem 1.25rem;
      }

      fieldset {
        border: none;
        margin: 0;
        padding: 0 0 1rem;
      }

      fieldset legend {
        font-size: 1rem;
        font-weight: 600;
        padding-bottom: 0.5rem;
      }

      fieldset > div {
        display: flex;
        gap: 0.5rem;
        flex-direction: column;
        align-items: baseline;
      }

      fieldset > div > span {
        align-items: center;
        display: flex;
        gap: 0.5rem;
      }

      .margin-percentage,
      .margin-percentage input {
        position: relative;
        width: 98px;
      }

      .margin-percentage label {
        background: var(--fg-gray--100, var(--fg-fallback-gray--100));
        border: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
        border-bottom-right-radius: var(
          --fg-borderradius--xs,
          var(--fg-fallback-borderradius--xs)
        );
        border-top-right-radius: var(
          --fg-borderradius--xs,
          var(--fg-fallback-borderradius--xs)
        );
        color: color-mix(
          in srgb,
          var(--fg-gray--900, var(--fg-fallback-gray--900)) 50%,
          transparent
        );
        display: grid;
        height: 48px;
        place-content: center;
        position: absolute;
        right: 0;
        top: 0;
        width: 28px;
      }

      button.button-cta {
        align-items: center;
        display: flex;
        flex-wrap: nowrap;
        gap: 0.25rem;
        white-space: nowrap;
      }

      button.button-cta svg {
        width: 22px;
        height: 22px;
      }

      .remarks {
        display: grid;
        gap: 0.5rem;
        padding-top: 0.5rem;
      }

      .remarks label {
        display: block;
        font-weight: 600;
        padding-bottom: 0.5rem;
      }

      .remarks textarea {
        display: block;
        height: 140px;
        width: 100%;
      }

      .collection-contents {
        padding-top: 1.5rem;
      }

      .table-header {
        color: var(--fg-gray--600, var(--fg-fallback-gray--600));
        display: none;
        font-size: 0.75rem;
        grid-template-columns: 1fr 190px 190px 64px;
        padding: 0 0.75rem 0.5rem;
      }

      .table-header span ~ span {
        text-align: right;
      }

      .table-row fg-image::part(image) {
        border-radius: var(
          --fg-borderradius--xs,
          var(--fg-fallback-borderradius--xs)
        );
      }

      .table-row {
        display: grid;
        grid-template-columns: 1fr;
        padding: 0.75rem;
        position: relative;
      }

      .table-row:nth-child(odd) {
        background-color: var(--fg-gray--100, var(--fg-fallback-gray--100));
        border-radius: var(
          --fg-borderradius--sm,
          var(--fg-fallback-borderradius--sm)
        );
      }

      .table-row > span:first-of-type {
        gap: 0.5rem;
        grid-template-columns: 64px 1fr;
      }

      .table-row span:first-of-type span {
        color: var(--fg-gray--500, var(--fg-fallback-gray--500));
        font-size: 0.875rem;
        grid-template-rows: 1fr;
        grid-template-columns: 1fr;
        width: 100%;
      }

      .table-row span:first-of-type span + span {
        color: var(--fg-gray--800, var(--fg-fallback-gray--800));
        font-size: 1rem;
        font-weight: 600;
      }

      .table-row span {
        align-items: center;
        display: grid;
      }

      .table-row span ~ span {
        justify-content: end;
      }

      .table-row span button {
        color: var(--fg-error--dark, var(--fg-fallback-error--dark));
        display: grid;
        height: 24px;
        place-items: center;
        position: absolute;
        top: 12px;
        right: 12px;
        width: 24px;
      }

      .table-row span.price {
        align-items: flex-end;
        display: flex;
        gap: 0.5rem;
        justify-content: space-between;
        justify-self: end;
      }

      .price > span:first-child {
        color: var(--fg-gray--600, var(--fg-fallback-gray--600));
        display: inline;
        font-size: 0.75rem;
      }

      fg-pagination {
        margin-top: 1.5rem;
      }

      @media screen and (min-width: ${BREAKPOINT_EXTRA_LARGE_PX}px) {
        .table-header {
          display: grid;
        }

        .table-row {
          align-items: center;
          grid-template-columns: 1fr 190px 190px 64px;
        }

        .price > span:first-child {
          display: none;
        }

        .table-row span button {
          position: relative;
          top: 0;
          right: 0;
        }

        .remarks {
          grid-template-columns: 1fr 1fr;
        }

        fieldset > div {
          flex-direction: row;
          align-items: center;
        }

        fieldset {
          padding-bottom: 0;
        }
      }
    `,
  ];
}
